import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

export const getElementCoordinatesBySelector = (target: any, selector?: string, alignCenter?: boolean) => {
    const elements = selector ? target?.querySelectorAll(selector) : [target];
    const element = elements?.[0];

    if (typeof element?.getBoundingClientRect === 'function') {
        const rect = element.getBoundingClientRect();

        return {
            x: alignCenter ? rect.left + rect.width / 2 : rect.left,
            y: rect.top,
            width: rect.width,
            height: rect.height,
        };
    }

    return undefined;
};

export const useNodeByRefCallback = (query?: string, isAncestor = true) => {
    const [node, setNode] = useState(null);
    const ref = useCallback(
        (currentNode) => {
            if (currentNode !== null) {
                setNode(query ? currentNode[isAncestor ? 'closest' : 'querySelector'](`.${query}`) : currentNode);
            }
        },
        [isAncestor, query],
    );

    return [node, ref];
};

export const preventDefaultAndStopPropagation = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();
};

export const addClass = (e: React.MouseEvent<HTMLElement>, query: string, className: string, isRemove = false) => {
    const classList = (e.target as HTMLElement)?.closest(query)?.classList;

    if (classList) {
        classList[isRemove ? 'remove' : 'add'](className);
    }
};

export const returnsToBreaks = (str: string) => str.replace(/(\r\n|\r|\n)/g, '<br/>');

interface IUseAttributeChange {
    targetRef?: React.RefObject<any>;
    attributeFilter?: string[];
    onAttributeChange?: (attributeName: string, target: Node) => void;
}

export const useOnRefAttributeChange = (props: IUseAttributeChange) => {
    const { targetRef, attributeFilter = ['style'], onAttributeChange } = props;

    useEffect(() => {
        if (!targetRef?.current || !onAttributeChange) {
            return () => false;
        }

        const mutationCallback = (mutations: MutationRecord[]) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName && attributeFilter?.includes(mutation.attributeName)) {
                    onAttributeChange(mutation.attributeName, mutation.target);
                }
            });
        };

        const observer = new MutationObserver(mutationCallback);

        observer.observe(targetRef?.current, {
            attributes: true,
            attributeFilter,
        });

        return () => observer.disconnect();
    }, [attributeFilter, onAttributeChange, targetRef]);
};
